import { ethers } from 'ethers';
import TonWeb from 'tonweb';
import { getChainConfig, getChainNetwork, getMinGasAmount } from '../constants/chains';
import { BEP20_ABI } from '../constants/abis';

// Cache providers to avoid creating new instances
const providerCache = {
  TON: {
    testnet: null,
    mainnet: null
  },
  BNB: {
    testnet: null,
    mainnet: null
  }
};

export const validateAddress = (address, chainId, isTestnet = false) => {
  try {
    if (!address || typeof address !== 'string') {
      return false;
    }

    const cleanAddress = address.trim().replace(/\s+/g, '');
    if (!cleanAddress) {
      return false;
    }

    switch (chainId) {
      case 'TON': {
        try {
          // Just validate that it's a parseable TON address in workchain 0
          const addr = new TonWeb.Address(cleanAddress);
          return addr.workChain === 0;
        } catch (err) {
          console.warn('TON address validation error:', err);
          return false;
        }
      }
      
      case 'BNB': {
        try {
          if (!cleanAddress.startsWith('0x')) {
            return false;
          }
          // Validate and convert to checksum address
          const checksumAddress = ethers.utils.getAddress(cleanAddress);
          return true;
        } catch (err) {
          console.warn('BNB address validation error:', err);
          return false;
        }
      }
      
      default:
        return false;
    }
  } catch (error) {
    console.warn('Address validation error:', error);
    return false;
  }
};

export const formatBalance = (balance, chainId) => {
  try {
    // Handle empty or invalid balance
    if (!balance) return '0';
    
    // Convert balance to string if it's not already
    const balanceStr = balance.toString();

    // If no chainId provided, just format the number
    if (!chainId) {
      const num = parseFloat(balanceStr);
      return isNaN(num) ? '0' : num.toFixed(4);
    }

    // Ensure chainId is a string
    const chain = typeof chainId === 'string' ? chainId.toUpperCase() : String(chainId).toUpperCase();

    // Handle chain-specific formatting
    switch (chain) {
      case 'TON':
        return TonWeb.utils.fromNano(balanceStr);
      case 'BNB':
        return ethers.utils.formatUnits(balanceStr, 18); // BNB always uses 18 decimals
      default:
        // For unknown chains, format as a regular number
        const num = parseFloat(balanceStr);
        return isNaN(num) ? '0' : num.toFixed(4);
    }
  } catch (error) {
    console.error('Error formatting balance:', error);
    // Return the original balance as string if formatting fails
    return balance?.toString() || '0';
  }
};

export const parseAmount = (amount, chainId) => {
  try {
    if (!amount || !chainId) return '0';

    switch (chainId.toUpperCase()) {
      case 'TON':
        return TonWeb.utils.toNano(amount);
      case 'BNB':
        return ethers.utils.parseUnits(amount, 18); // BNB always uses 18 decimals
      default:
        console.warn(`Unknown chain ID: ${chainId}, returning '0'`);
        return '0';
    }
  } catch (error) {
    console.error('Error parsing amount:', error);
    return '0';
  }
};

export const getProvider = (chainId, isTestnet = false) => {
  try {
    const network = getChainNetwork(chainId, isTestnet);
    if (!network?.rpcUrl) throw new Error('Invalid network configuration');

    const networkType = isTestnet ? 'testnet' : 'mainnet';
    
    // Return cached provider if available
    if (providerCache[chainId]?.[networkType]) {
      return providerCache[chainId][networkType];
    }

    let provider;
    switch (chainId) {
      case 'TON':
        const httpProvider = new TonWeb.HttpProvider(network.rpcUrl);
        provider = new TonWeb(httpProvider);
        provider.httpProvider = httpProvider; // Attach HttpProvider for getInfo() access
        break;
      case 'BNB':
        // Use JsonRpcProvider directly without MetaMask dependency
        provider = new ethers.providers.JsonRpcProvider(network.rpcUrl);
        break;
      default:
        throw new Error('Unsupported chain');
    }

    // Cache the provider
    if (!providerCache[chainId]) {
      providerCache[chainId] = {};
    }
    providerCache[chainId][networkType] = provider;

    return provider;
  } catch (error) {
    console.error('Error getting provider:', error);
    throw error;
  }
};

export const clearProviderCache = (chainId, isTestnet) => {
  if (chainId) {
    const networkType = isTestnet ? 'testnet' : 'mainnet';
    if (providerCache[chainId]) {
      providerCache[chainId][networkType] = null;
    }
  } else {
    Object.keys(providerCache).forEach(chain => {
      providerCache[chain].testnet = null;
      providerCache[chain].mainnet = null;
    });
  }
};

export const estimateGasFee = async (chainId, from, to, amount, data = '') => {
  try {
    const provider = getProvider(chainId);
    
    switch (chainId) {
      case 'TON': {
        // TON gas estimation logic
        const minGas = getMinGasAmount(chainId);
        const estimatedGas = TonWeb.utils.toNano(minGas);
        
        // Add 10% buffer for safety using string operations for TON
        const gasWithBuffer = (parseFloat(TonWeb.utils.fromNano(estimatedGas)) * 1.1).toString();
        return {
          gas: estimatedGas,
          gasWithBuffer: TonWeb.utils.toNano(gasWithBuffer)
        };
      }
        
      case 'BNB': {
        // BNB gas estimation using ethers.BigNumber
        const [gasPrice, gasLimit] = await Promise.all([
          provider.getGasPrice(),
          provider.estimateGas({
            from,
            to,
            value: parseAmount(amount, chainId),
            data
          })
        ]);

        const gas = gasPrice.mul(gasLimit);
        // Add 10% buffer for safety using ethers.BigNumber
        const gasWithBuffer = gas.mul(110).div(100);
        
        return {
          gas,
          gasWithBuffer,
          gasPrice,
          gasLimit
        };
      }
        
      default:
        throw new Error('Unsupported chain');
    }
  } catch (error) {
    console.error('Error estimating gas fee:', error);
    throw error;
  }
};

export const getTokenContract = (tokenAddress, chainId, provider) => {
  try {
    switch (chainId) {
      case 'BNB':
        return new ethers.Contract(tokenAddress, BEP20_ABI, provider);
      case 'TON':
        // TON token contract implementation
        const wallet = provider.wallet(tokenAddress);
        return wallet.methods.getJettonData();
      default:
        throw new Error('Unsupported chain');
    }
  } catch (error) {
    console.error('Error getting token contract:', error);
    throw error;
  }
};

export const getExplorerTxUrl = (txHash, chainId, isTestnet = false) => {
  try {
    const network = getChainNetwork(chainId, isTestnet);
    return `${network.explorer}/tx/${txHash}`;
  } catch (error) {
    console.error('Error getting explorer URL:', error);
    return '';
  }
};

export const getExplorerAddressUrl = (address, chainId, isTestnet = false) => {
  try {
    const network = getChainNetwork(chainId, isTestnet);
    return `${network.explorer}/address/${address}`;
  } catch (error) {
    console.error('Error getting explorer URL:', error);
    return '';
  }
};

// New utility functions for core maintenance

export const validateTransaction = async (tx, chainId) => {
  try {
    const provider = getProvider(chainId);
    
    switch (chainId) {
      case 'TON':
        return await provider.getTransactionStatus(tx);
      case 'BNB':
        return await provider.getTransactionReceipt(tx);
      default:
        throw new Error('Unsupported chain');
    }
  } catch (error) {
    console.error('Error validating transaction:', error);
    throw error;
  }
};

export const getTokenAllowance = async (tokenAddress, ownerAddress, spenderAddress, chainId) => {
  try {
    const provider = getProvider(chainId);
    
    switch (chainId) {
      case 'BNB': {
        const contract = new ethers.Contract(tokenAddress, BEP20_ABI, provider);
        const allowance = await contract.allowance(ownerAddress, spenderAddress);
        return allowance.toString();
      }
      case 'TON':
        // TON doesn't use allowances in the same way
        return null;
      default:
        throw new Error('Unsupported chain');
    }
  } catch (error) {
    console.error('Error getting token allowance:', error);
    throw error;
  }
};

export const isContractAddress = async (address, chainId) => {
  try {
    const provider = getProvider(chainId);
    
    switch (chainId) {
      case 'BNB': {
        const code = await provider.getCode(address);
        return code !== '0x';
      }
      case 'TON': {
        const code = await provider.getContractInfo(new TonWeb.Address(address));
        return code && code.code;
      }
      default:
        throw new Error('Unsupported chain');
    }
  } catch (error) {
    console.error('Error checking contract address:', error);
    return false;
  }
};

export const validateTokenAddress = (tokenAddress, chainId, isTestnet = false) => {
  try {
    if (!tokenAddress || typeof tokenAddress !== 'string') {
      return false;
    }

    switch (chainId) {
      case 'TON':
        try {
          // For TON tokens (Jettons), validate and normalize the address
          const addr = new TonWeb.Address(tokenAddress.trim());
          // Just ensure it's a valid TON address in workchain 0
          return addr.workChain === 0;
        } catch (err) {
          console.warn('TON token address validation error:', err);
          return false;
        }
      
      case 'BNB':
        try {
          // For BNB tokens (BEP20), validate the address format
          const cleanAddress = tokenAddress.trim();
          if (!cleanAddress.startsWith('0x') || cleanAddress.length !== 42) {
            return false;
          }
          // Use ethers.js checksum validation
          return ethers.utils.getAddress(cleanAddress) === cleanAddress;
        } catch (err) {
          console.warn('BNB token address validation error:', err);
          return false;
        }
      
      default:
        return false;
    }
  } catch (error) {
    console.warn('Token address validation error:', error);
    return false;
  }
};

export const normalizeTokenAddress = (tokenAddress, chainId, isTestnet = false) => {
  try {
    if (!tokenAddress || typeof tokenAddress !== 'string') {
      return null;
    }

    const cleanAddress = tokenAddress.trim();
    if (!cleanAddress) return null;

    switch (chainId) {
      case 'TON':
        try {
          // Normalize TON token address to standard format
          const addr = new TonWeb.Address(cleanAddress);
          return addr.toString();
        } catch (err) {
          console.warn('TON token address normalization error:', err);
          return null;
        }
      
      case 'BNB':
        try {
          // Normalize BNB token address to checksum format
          return ethers.utils.getAddress(cleanAddress);
        } catch (err) {
          console.warn('BNB token address normalization error:', err);
          return null;
        }
      
      default:
        return null;
    }
  } catch (error) {
    console.warn('Token address normalization error:', error);
    return null;
  }
};

export const normalizeAddress = (address, chainId, isTestnet = false) => {
  try {
    if (!address || typeof address !== 'string') {
      return null;
    }

    const cleanAddress = address.trim().replace(/\s+/g, '');
    if (!cleanAddress) {
      return null;
    }

    switch (chainId) {
      case 'TON': {
        try {
          const addr = new TonWeb.Address(cleanAddress);
          // Always return bounceable format for user wallets
          return addr.toString(true, true, true);
        } catch (err) {
          console.warn('TON address normalization error:', err);
          return null;
        }
      }
      
      case 'BNB': {
        try {
          return ethers.utils.getAddress(cleanAddress);
        } catch (err) {
          console.warn('BNB address normalization error:', err);
          return null;
        }
      }

      default:
        return null;
    }
  } catch (error) {
    console.warn('Address normalization error:', error);
    return null;
  }
};

export const estimateGas = async (fromToken, toToken, amount) => {
  try {
    const gasPrice = {
      TON: 0.05, // TON gas price in TON
      BNB: 0.0001 // BNB gas price in BNB
    };

    const chain = fromToken.chain;
    const baseGas = getChainConfig(chain).minGasAmount;
    const estimatedGas = parseFloat(baseGas) * (1 + (parseFloat(amount) * 0.01));
    
    // Get current token prices
    const response = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${fromToken.coingeckoId}&vs_currencies=usd`);
    const data = await response.json();
    const tokenPrice = data[fromToken.coingeckoId]?.usd || 0;

    return {
      amount: estimatedGas.toFixed(6),
      inUSD: (estimatedGas * tokenPrice).toFixed(2)
    };
  } catch (error) {
    console.error('Error estimating gas:', error);
    return {
      amount: getChainConfig(fromToken.chain).minGasAmount,
      inUSD: '0.00'
    };
  }
};

export const calculatePriceImpact = (fromAmount, fromPrice, toAmount, toPrice) => {
  try {
    const inputValue = fromAmount * fromPrice;
    const outputValue = toAmount * toPrice;
    return Math.abs((outputValue - inputValue) / inputValue) * 100;
  } catch (error) {
    console.error('Error calculating price impact:', error);
    return 0;
  }
};

export const getTokenPriceWithFallback = async (coingeckoId, symbol) => {
  try {
    const response = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${coingeckoId}&vs_currencies=usd&include_24hr_change=true`);
    const data = await response.json();
    
    return {
      price: data[coingeckoId]?.usd || 0,
      change24h: data[coingeckoId]?.usd_24h_change || 0
    };
  } catch (error) {
    console.error(`Error fetching price for ${symbol}:`, error);
    return {
      price: 0,
      change24h: 0
    };
  }
}; 

export const validateChainConfig = (chainId) => {
  try {
    if (!chainId || typeof chainId !== 'string') {
      return {
        isValid: false,
        error: 'Chain ID must be a non-empty string'
      };
    }

    // Normalize chain ID
    const normalizedChainId = chainId.toUpperCase();
    
    // Get chain configuration
    const config = getChainConfig(normalizedChainId);
    if (!config) {
      return {
        isValid: false,
        error: `Chain configuration not found for ${chainId}`
      };
    }

    // Required fields for a valid chain configuration
    const requiredFields = [
      'name',
      'symbol',
      'decimals',
      'testnet',
      'mainnet',
      'features'
    ];

    // Required subfields for network configurations
    const networkFields = ['rpcUrl', 'explorer', 'chainId'];

    // Check all required fields
    const missingFields = [];
    
    for (const field of requiredFields) {
      if (field === 'testnet' || field === 'mainnet') {
        if (!config[field] || typeof config[field] !== 'object') {
          missingFields.push(field);
        } else {
          // Check network specific fields
          for (const netField of networkFields) {
            if (!config[field][netField]) {
              missingFields.push(`${field}.${netField}`);
            }
          }
        }
      } else if (!config[field]) {
        missingFields.push(field);
      }
    }

    return {
      isValid: missingFields.length === 0,
      missingFields: missingFields.length > 0 ? missingFields : undefined,
      chainId: normalizedChainId,
      config
    };
  } catch (error) {
    console.error('Chain config validation failed:', error);
    return {
      isValid: false,
      error: error.message
    };
  }
}; 